import { cn } from '@/lib/utils';
import { UploadSolid } from '@mynaui/icons-react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploadItem } from './FileList';
import { Button } from './ui/button';

interface AdvancedUploadProps {
  onFilesAdded?: (files: FileUploadItem[]) => void;
}

// Hard cap so an accidental drop of a huge directory (e.g. a home folder)
// can't OOM the tab while we walk it.
const MAX_DROPPED_FILES = 50_000;

const AdvancedUpload: React.FC<AdvancedUploadProps> = ({ onFilesAdded }) => {
  const { t } = useTranslation('components');
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const directoryInputRef = useRef<HTMLInputElement>(null);

  const processFiles = (files: FileList, isDirectory = false) => {
    const fileData = Array.from(files).map(
      (file): FileUploadItem => ({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        path: file.webkitRelativePath || file.name,
        size: file.size,
        type: file.type,
        file: file,
        isDirectory: isDirectory,
      }),
    );

    if (onFilesAdded) onFilesAdded(fileData);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setIsProcessing(true);

    // Capture entries synchronously: DataTransferItemList is invalidated as
    // soon as the drop handler returns control to the event loop. Use a plain
    // for-loop with try/catch to defend against Firefox builds where
    // webkitGetAsEntry can throw or where iterating items via Array.from is
    // flaky for folder drops.
    const dt = e.dataTransfer;
    const entries: FileSystemEntry[] = [];
    if (dt?.items) {
      for (let i = 0; i < dt.items.length; i++) {
        const item = dt.items[i];
        if (!item || item.kind !== 'file') continue;
        try {
          const entry = item.webkitGetAsEntry?.();
          if (entry) entries.push(entry);
        } catch {
          // Some Firefox versions throw on webkitGetAsEntry; skip and continue.
        }
      }
    }

    try {
      // No FileSystemEntry support / no folder drop — fall back to plain files
      // so single-file drops still work on browsers without webkitGetAsEntry.
      if (entries.length === 0) {
        if (dt?.files && dt.files.length > 0) processFiles(dt.files, false);
        return;
      }

      const allFiles: { file: File; name: string; fullPath: string }[] = [];

      // Process top-level entries sequentially. Within each directory,
      // traverseFileTree also walks sequentially. The original code used
      // Promise.all over every sibling, which on large folder drops (10k+
      // files) fans out thousands of concurrent FileSystemEntry reads and
      // crashes Firefox via memory exhaustion. Sequential traversal keeps
      // peak memory bounded.
      for (const entry of entries) {
        await traverseFileTree(entry, allFiles, '');
        if (allFiles.length > MAX_DROPPED_FILES) break;
      }

      if (allFiles.length === 0) return;

      const truncated = allFiles.length > MAX_DROPPED_FILES;
      const limited = truncated ? allFiles.slice(0, MAX_DROPPED_FILES) : allFiles;
      if (truncated) {
        console.warn(
          `Dropped folder contained more than ${MAX_DROPPED_FILES} files; only the first ${MAX_DROPPED_FILES} were added.`,
        );
      }

      const fileData: FileUploadItem[] = limited.map((fileInfo) => ({
        id: Math.random().toString(36).substr(2, 9),
        name: fileInfo.name,
        path: fileInfo.fullPath,
        size: fileInfo.file.size,
        type: fileInfo.file.type,
        file: fileInfo.file,
        isDirectory: true,
      }));

      if (onFilesAdded) onFilesAdded(fileData);
    } catch (error) {
      console.error('Error processing dropped files:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const traverseFileTree = (
    entry: FileSystemEntry,
    allFiles: { file: File; name: string; fullPath: string }[],
    path = '',
  ): Promise<void> => {
    return new Promise<void>((resolve) => {
      // Always pass an error callback to entry.file() and readEntries() so a
      // single unreadable file or folder (permissions, symlinks, system
      // entries) cannot leave the Promise pending — that's how Firefox ends
      // up holding millions of in-flight FileSystemEntry handles and OOM-ing.
      if (entry.isFile) {
        (entry as FileSystemFileEntry).file(
          (file) => {
            allFiles.push({ file, name: file.name, fullPath: path + file.name });
            resolve();
          },
          () => resolve(),
        );
      } else if (entry.isDirectory) {
        const dirReader = (entry as FileSystemDirectoryEntry).createReader();
        const readBatch = () => {
          dirReader.readEntries(
            async (batch) => {
              if (batch.length === 0) {
                resolve();
                return;
              }
              try {
                // Sequential rather than Promise.all: keeps the active set of
                // file handles small enough for Firefox to cope with deep or
                // wide directory trees.
                for (const child of batch) {
                  if (allFiles.length > MAX_DROPPED_FILES) break;
                  await traverseFileTree(child, allFiles, path + entry.name + '/');
                }
              } catch (err) {
                console.error('Error traversing entry:', entry.name, err);
              }
              if (allFiles.length > MAX_DROPPED_FILES) {
                resolve();
                return;
              }
              readBatch();
            },
            () => resolve(),
          );
        };
        readBatch();
      } else {
        resolve();
      }
    });
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'border flex flex-col items-center border-dashed rounded-lg pt-8 pb-6 text-center mb-5 relative transition-colors w-full select-none',
        isDragging
          ? 'border-blue-500 bg-blue-50 dark:bg-slate-900 dark:border-slate-800'
          : 'border-gray-300/70 bg-gray-50 dark:bg-stone-900 dark:border-stone-800',
      )}>
      <UploadSolid className='size-12 text-gray-400 dark:text-stone-600 mb-4' />
      {isProcessing && (
        <div className='absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg'>
          <div className='text-gray-700'>{t('advancedUpload.processingFiles')}</div>
        </div>
      )}

      <h3 className='text-lg font-semibold'>{t('advancedUpload.dropFiles')}</h3>
      <p className='text-sm lowercase text-gray-600 dark:text-stone-500 mb-4'>{t('advancedUpload.orUseButtons')}</p>

      <div className='flex justify-center items-center gap-2'>
        <Button variant='ghost' onClick={() => fileInputRef.current?.click()} disabled={isProcessing}>
          {t('advancedUpload.selectFiles')}
        </Button>
        <span className='text-xs font-medium text-gray-500 dark:text-stone-500'>{t('advancedUpload.or')}</span>
        <Button variant='ghost' onClick={() => directoryInputRef.current?.click()} disabled={isProcessing}>
          {t('advancedUpload.selectDirectory')}
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type='file'
        multiple
        className='hidden'
        onChange={(e) => {
          if (e.target.files) {
            processFiles(e.target.files, false);
          }
          e.target.value = '';
        }}
      />

      <input
        ref={directoryInputRef}
        type='file'
        {...({ webkitdirectory: '' } as any)}
        className='hidden'
        onChange={(e) => {
          if (e.target.files) {
            processFiles(e.target.files, true);
          }
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default AdvancedUpload;
